import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminFranchisesQueryKey,
  useGetSuperAdminFranchises,
  usePostSuperAdminFranchises,
} from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { BUTTON_PRIMARY, PageHeader, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { Building2 } from 'lucide-react'

import {
  COLOUR_PATTERN,
  EMPTY_DRAFT,
  createPayload,
  creationConfirmed,
  mayLeave,
  createdFranchiseIdOf,
  notesOf,
  provisioningSummary,
  slugify,
  STEPS,
  stepAfter,
  stepLabel,
  stepProblem,
  type NewFranchiseDraft,
  type Step,
} from './new-franchise'
import { setupNeededTitles } from './integrations'

/**
 * The provisioning wizard — the one screen here that can create infrastructure.
 *
 * The two switches that do so start **off**, the last step says in words what each will do, and creating
 * asks for the slug typed out — the same weight as erasing, in the other direction. SAAS.md §5:
 * *"a 'just testing' franchise on prod NPM is an incident."*
 */
export function NewFranchisePage() {
  const [, navigate] = useLocation()
  const queryClient = useQueryClient()
  const existing = useGetSuperAdminFranchises()
  const create = usePostSuperAdminFranchises()

  const [draft, setDraft] = useState<NewFranchiseDraft>(EMPTY_DRAFT)
  const [step, setStep] = useState<Step>('basic')
  const [typed, setTyped] = useState('')
  const [notes, setNotes] = useState<string[]>([])
  const [createdId, setCreatedId] = useState<number | null>(null)

  // §3.107 - **`null` when the read failed**, not an empty list. This feeds the slug uniqueness
  // check, and an empty list makes that check pass on every slug: the wizard would provision a
  // franchise whose slug is a duplicate database name, which is the one thing `stepProblem`'s own
  // comment says must be refused here rather than half-provisioned.
  const slugs = useMemo(() => {
    if (existing.isError) return null
    const rows = Array.isArray((existing.data as { franchises?: unknown } | undefined)?.franchises)
      ? (existing.data as { franchises: Record<string, unknown>[] }).franchises
      : []
    return rows.map((row) => (typeof row.slug === 'string' ? row.slug : ''))
  }, [existing.data, existing.isError])

  const problem = stepProblem(step, draft, slugs)

  function set(patch: Partial<NewFranchiseDraft>) {
    setDraft({ ...draft, ...patch })
  }

  return (
    <main className="space-y-3 max-w-xl" data-new-franchise data-step={step}>
      <PageHeader
        title={tt('Nová franšíza')}
        description={tt('Založenie pobočky: údaje, databáza, balík.')}
        icon={<Building2 className="w-5 h-5" />}
      />
      {/* Which of the four you are on - §3.102. Legacy draws this row and AGOS drew nothing, so an
          operator four fields into a multi-part form had no idea how much was left. Built from
          `STEPS`, which until now was an exported list **read by nothing** - and whose fifth member
          was a step that rendered a blank screen. */}
      <ol className="flex flex-wrap items-center gap-1.5" data-step-progress={String(STEPS.length)}>
        {STEPS.map((one, index) => {
          const reached = STEPS.indexOf(step) >= index
          return (
            <li key={one} className="flex items-center gap-1.5">
              <span
                data-step-dot={one}
                data-step-dot-state={one === step ? 'current' : reached ? 'done' : 'todo'}
                className={cn(
                  'px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider',
                  one === step
                    ? 'border-primary bg-primary/10 text-primary'
                    : reached
                      ? 'border-border text-foreground'
                      : 'border-border text-muted-foreground',
                )}
              >
                {stepLabel(one)}
              </span>
              {index < STEPS.length - 1 && <span className="h-px w-3 bg-border" />}
            </li>
          )
        })}
      </ol>

      {step === 'basic' && (
        <section className="space-y-2" data-step-basic>
          <input
            data-field="name"
            value={draft.name}
            onChange={(event) => set({ name: event.target.value, slug: slugify(event.target.value) })}
            placeholder={tt('Názov franšízy')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
          <input
            data-field="slug"
            value={draft.slug}
            onChange={(event) => set({ slug: event.target.value })}
            placeholder={tt('Skratka')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm font-mono"
          />
          {/* §3.87 - **the draft has carried `displayName` and sent it since this form was written,
            * and nothing typed it.** `FRANCHISE_COLUMNS` maps it, the `INSERT` names it and the read
            * hands it back, so it was permanently `undefined` through `orUndefined`. Legacy collects
            * it on this same step as *Display name (voliteľné)*. */}
          <input
            data-field="display-name"
            value={draft.displayName}
            onChange={(event) => set({ displayName: event.target.value })}
            placeholder={tt('Zobrazovaný názov, ak sa má líšiť od názvu')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
          <input
            data-field="domain"
            value={draft.domain}
            onChange={(event) => set({ domain: event.target.value })}
            placeholder={tt('Doména, napríklad nitra.pizzamajstro.sk')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
          {/* §3.115 - the brand, which legacy collects here with these two defaults and this wizard did
            * not collect at all. `CentralRegistryService` serves both to the customer app, where
            * `branding.ts` writes them as CSS variables, so a franchise created here went live with a
            * null brand until somebody remembered the detail screen. A colour input beside the text one:
            * the picker is how somebody chooses a colour and the text is how they paste a brand's. */}
          <div className="flex flex-wrap gap-2" data-brand-colours>
            {(['primaryColor', 'secondaryColor'] as const).map((key) => (
              <label key={key} className="text-[11px]">
                <span className="text-muted-foreground">
                  {key === 'primaryColor' ? tt('Primárna farba') : tt('Sekundárna farba')}
                </span>
                <span className="mt-1 flex items-center gap-1">
                  <input
                    type="color"
                    aria-label={key === 'primaryColor' ? tt('Primárna farba') : tt('Sekundárna farba')}
                    data-field-colour-picker={key}
                    value={COLOUR_PATTERN.test(draft[key].trim()) ? draft[key].trim() : '#000000'}
                    onChange={(event) => set({ [key]: event.target.value })}
                    className="h-9 w-9 rounded-lg border border-border bg-background"
                  />
                  <input
                    data-field={key}
                    value={draft[key]}
                    onChange={(event) => set({ [key]: event.target.value })}
                    placeholder="#f97316"
                    className="min-h-[36px] w-28 rounded-lg border border-border bg-background px-2 font-mono text-xs"
                  />
                </span>
              </label>
            ))}
          </div>
        </section>
      )}

      {step === 'admin' && (
        <section className="space-y-2" data-step-admin>
          <input
            data-field="admin-email"
            type="email"
            value={draft.adminEmail}
            onChange={(event) => set({ adminEmail: event.target.value })}
            placeholder={tt('E-mail administrátora')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
          <input
            data-field="admin-password"
            type="password"
            autoComplete="new-password"
            value={draft.adminPassword}
            onChange={(event) => set({ adminPassword: event.target.value })}
            placeholder={tt('Heslo, aspoň 6 znakov')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
          {/* §3.87 - **without this every administrator is named by their e-mail address.**
            * `FranchiseAdminService.create` reads `adminName` and falls back to the e-mail when it is
            * absent, and it was always absent: the draft carried the field and this step had two
            * inputs. Legacy asks for it here as *Meno (voliteľné)*. */}
          <input
            data-field="admin-name"
            value={draft.adminName}
            onChange={(event) => set({ adminName: event.target.value })}
            placeholder={tt('Meno administrátora, nepovinné')}
            className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
          />
        </section>
      )}

      {step === 'provisioning' && (
        <section className="space-y-2" data-step-provisioning>
          {/* §3.16 - the field the server refuses to create a franchise without, and where the two
            * switches used to be. They sent `autoProvision` and `deployStack`, which appear nowhere
            * in the backend: this platform will not create infrastructure from an API call (§5), so
            * the honest step is to paste the URL of the stack that was already deployed. */}
          <label className="block text-xs space-y-1">
            <span className="font-semibold">
              <T>Adresa databázy franšízy</T>
            </span>
            <input
              data-field="database-url"
              value={draft.databaseUrl}
              onChange={(event) => set({ databaseUrl: event.target.value })}
              placeholder="postgresql://user:pass@host:5432/franchise_nitra"
              className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
            />
          </label>
          <p className="text-[11px] text-muted-foreground" data-database-url-note>
            <T>
              Stack sa nasadzuje podľa INSTRUCTIONS.md pred zápisom franšízy - sem sa vloží adresa
              databázy, ktorá už existuje. Heslo sa späť nevypisuje.
            </T>
          </p>

          <ul className="text-[11px] text-muted-foreground space-y-0.5" data-provisioning-summary>
            {provisioningSummary(draft).map((line, index) => (
              <li key={index} data-summary-line>
                {line}
              </li>
            ))}
          </ul>

          <input
            data-confirm-input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={draft.slug}
            className="w-full min-h-[40px] rounded-xl border border-border bg-background px-3 text-xs"
          />
          <button
            type="button"
            data-create
            disabled={!creationConfirmed(typed, draft.slug) || create.isPending}
            onClick={() => {
              void create
                .mutateAsync({ data: createPayload(draft) })
                .then((answer) => {
                  /**
                   * The list this wizard just changed - SAAS.md §3.64, FB-663.
                   *
                   * **`existing` above and `FranchisesPage` are one cache entry**: both call
                   * `useGetSuperAdminFranchises()` with no arguments, so the key is the same, and
                   * `App.tsx` sets `staleTime: 30_000`. The slug check has to read that list on the
                   * way in - so without this, an operator who creates a tenant and clicks *Na zoznam
                   * franšíz* inside thirty seconds lands on a list that does not contain it. A mount
                   * does not refetch data React Query still considers fresh.
                   *
                   * **Here rather than on the navigation button**, because the row exists the moment
                   * the server answers and the operator may leave by the shell's own link, reload, or
                   * not leave at all.
                   */
                  void queryClient.invalidateQueries({
                    queryKey: getGetSuperAdminFranchisesQueryKey(),
                  })
                  setNotes(notesOf(answer))
                  // §3.136 - so the result step can send the operator where the credentials are set.
                  setCreatedId(createdFranchiseIdOf(answer))
                  setStep('result')
                })
                .catch((failure: unknown) =>
                  toast({
                    title: apiErrorMessage(failure, tt('Franšízu sa nepodarilo vytvoriť.')),
                    variant: 'destructive',
                  }),
                )
            }}
            className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-40"
          >
            <T>Vytvoriť franšízu</T>
          </button>
        </section>
      )}

      {step === 'result' && (
        <section className="space-y-2" data-step-result>
          <p className="text-sm font-bold">
            <T>Hotovo</T>
          </p>
          {/* Verbatim: a run that skipped a step says so, and that is the only record of what exists. */}
          <ul className="text-[11px] space-y-0.5" data-result-notes>
            {notes.map((note, index) => (
              <li key={index} data-result-note>
                {note}
              </li>
            ))}
          </ul>
          {/* §3.136 - **a franchise can be created with no till, no card payments and no e-mail, and nothing
            * said so.** Legacy's wizard walks an operator through all four while creating the tenant; AGOS's
            * creation endpoint accepts no integration field, so the step legacy has cannot exist here (§3.102
            * removed the stub correctly). What was missing is the *saying* - and a way to get there. */}
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1.5">
            <p className="text-xs font-bold text-amber-500" data-setup-needed>
              <T>Ešte treba nastaviť</T>
            </p>
            <ul className="text-[11px] space-y-0.5">
              {setupNeededTitles().map((title) => (
                <li key={title} data-setup-needed-item>
                  {title}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground">
              <T>Bez nich franšíza nevytlačí doklad, neprijme platbu kartou a nepošle e-mail.</T>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {createdId !== null && (
              <button
                type="button"
                data-to-integrations
                onClick={() => navigate(`/franchises/${String(createdId)}/integrations`)}
                className={cn(
                  'min-h-[40px] px-3 rounded-xl text-xs',
                  BUTTON_PRIMARY,
                )}
              >
                <T>Nastaviť integrácie</T>
              </button>
            )}
            <button
              type="button"
              data-to-franchises
              onClick={() => navigate('/franchises')}
              className="min-h-[40px] px-3 rounded-xl border border-border text-xs font-semibold"
            >
              <T>Na zoznam franšíz</T>
            </button>
          </div>
        </section>
      )}

      {problem !== '' && (
        <p className="text-xs text-red-500" data-step-problem>
          {problem}
        </p>
      )}

      {step !== 'result' && (
        <div className="flex gap-2">
          <button
            type="button"
            data-next
            disabled={!mayLeave(step, draft, slugs)}
            onClick={() => {
              // `stepAfter` rather than a second list of the order - the list that used to be here
              // is where the dead `'integrations'` step survived after nobody rendered it.
              const next = stepAfter(step)
              if (next !== null) setStep(next)
            }}
            className="min-h-[40px] px-4 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
          >
            <T>Pokračovať</T>
          </button>
        </div>
      )}
    </main>
  )
}
